import React, { useEffect } from "react";
import { createPortal } from "react-dom";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Text from "components/atoms/text/Text";

import Constants from "utils/constants";

let openedModalCount = 0;
let previousBodyOverflow = "";
let previousBodyPaddingRight = "";

const lockBodyScroll = () => {
    if (typeof document === "undefined") {
        return;
    }

    const body = document.body;

    if (openedModalCount === 0) {
        previousBodyOverflow = body.style.overflow;
        previousBodyPaddingRight = body.style.paddingRight;

        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        body.style.overflow = "hidden";

        if (scrollbarWidth > 0) {
            body.style.paddingRight = `${scrollbarWidth}px`;
        }
    }

    openedModalCount += 1;
};

const unlockBodyScroll = () => {
    if (typeof document === "undefined" || openedModalCount === 0) {
        return;
    }

    openedModalCount -= 1;

    if (openedModalCount === 0) {
        document.body.style.overflow = previousBodyOverflow;
        document.body.style.paddingRight = previousBodyPaddingRight;
    }
};

const Components = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 20px 24px;
    overflow-y: auto;
    overscroll-behavior: contain;
    z-index: 100;

    ${Constants.BLUR_OPTION}
`;

const RealBox = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: min(${(props) => props.width}px, calc(100vw - 48px));
    max-width: calc(100vw - 48px);
    height: ${(props) => (props.height ? `${props.height}px` : "auto")};
    max-height: calc(100vh - 40px);
    padding: 24px;
    border-radius: 12px;
    border: solid 1px #bac9d0;
    background-color: var(--color-White);
    overflow: hidden;
`;

const InnerWrap = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    background-color: var(--color-White);
`;

const ModalHeader = styled.div`
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: auto;
`;

const ModalTitleText = styled(Text).attrs({
    variant: "title-sm-500",
})``;

const CloseButton = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background-color: transparent;
    transition: all 100ms ease-in;
    cursor: pointer;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const ContentsWrap = styled.div`
    box-sizing: border-box;
    margin: 24px 0 0 0;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding-right: 4px;
`;

function AbstractModal({ modalTitle, width, height, children, exitModal, id }) {
    useEffect(() => {
        lockBodyScroll();

        return () => {
            unlockBodyScroll();
        };
    }, []);

    return (
        <>
            {createPortal(
                <Components className="modalContainer" id={id}>
                    <RealBox width={width} height={height}>
                        <InnerWrap>
                            <ModalHeader>
                                <ModalTitleText>{modalTitle}</ModalTitleText>
                                <CloseButton onClick={exitModal}>
                                    <Icon name={"x"} color={"var(--color-SubBlack)"} />
                                </CloseButton>
                            </ModalHeader>
                            <ContentsWrap>{children}</ContentsWrap>
                        </InnerWrap>
                    </RealBox>
                </Components>,
                document.body,
            )}
        </>
    );
}

export default AbstractModal;
